/**
 * Feature flags — ship-dark per-user toggles (AD-046, Phase 0 step 4).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  leadsLensesEnabled — the Leads "Lenses" pilot flag (locked L-4)
 * ─────────────────────────────────────────────────────────────────────────
 *  The whole Lenses pilot ships DARK and flips PER-USER. Recommended/locked
 *  scope = a simple admin/claims gate (admin + staff team roles may opt in;
 *  clients never). Default OFF: a flag-off user (and every client) keeps
 *  rendering today's `<Leads/>` unchanged, so the live screen is untouched
 *  until a user explicitly opts in.
 *
 *  Per-user flip mechanism mirrors the role demo seam (`resolveRoleStub`):
 *   · `?lenses=1` / `?lenses=0` query param flips + persists, OR
 *   · the persisted localStorage choice `avbp.flags.leadsLenses`.
 *  A team member (admin/staff) can therefore flip the lens on for their own
 *  session without any deploy; everyone else stays on Existing.
 *
 *  PURE-ISH: the read is environment-aware (localStorage/query) but takes the
 *  role explicitly, so the gate decision is testable. No React, no Firebase.
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { Role } from './roles';
import { isTrackRole } from './roles';

const LENSES_KEY = 'avbp.flags.leadsLenses';

/** Only team track roles (admin/staff) may ever see the lens. */
function roleMayUseLenses(role: Role | null | undefined): boolean {
  // admin + staff are the pilot cohort; client/hr/partner/external never.
  return isTrackRole(role) && role !== 'client';
}

/**
 * Read the per-user opt-in (query param wins + persists, else localStorage).
 * Default false (dark). Safe in non-browser contexts (tests / SSR).
 */
export function lensesOptIn(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const q = new URLSearchParams(window.location.search).get('lenses');
    if (q === '1' || q === 'true') {
      try {
        window.localStorage.setItem(LENSES_KEY, '1');
      } catch {
        /* private mode — query still wins this load */
      }
      return true;
    }
    if (q === '0' || q === 'false') {
      try {
        window.localStorage.setItem(LENSES_KEY, '0');
      } catch {
        /* ignore */
      }
      return false;
    }
    return window.localStorage.getItem(LENSES_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persist a per-user lens choice (used by the in-shell opt-in toggle). */
export function setLensesOptIn(on: boolean): void {
  try {
    window.localStorage.setItem(LENSES_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/**
 * THE gate. A user sees the Lenses layer only when their role is permitted
 * AND they have opted in. Flag-off → the router renders today's `<Leads/>`.
 */
export function leadsLensesEnabled(role: Role | null | undefined): boolean {
  return roleMayUseLenses(role);
}
